/**
 * shadowCaster - Schattenpolygone aus dem SceneModel (#605)
 *
 * Reine, WebGL-/Fabric-freie Geometrie (Jest-testbar wie der übrige
 * scene3d-Kern). Konsumiert das SceneModel (#600) und eine Sonnenrichtung
 * (bereits um northAngle gedreht, aus scene3d/sunlight.js) und liefert die
 * projizierten Schattenflächen in Weltkoordinaten (Meter, x/z).
 *
 * Verfahren (PLAN_SCHATTENWURF_291 §3.1):
 *  1. Horizontaler Schattenversatz pro Höhe: offset = -sunDir_xz · h / sunDir_y
 *     (aus shadowLength = h / tan(altitude), Richtung = Gegenrichtung der Sonne)
 *  2. Footprint auf Boden projizieren: Nahkante bei baseY, Fernkante bei
 *     baseY + heightM (verlängert bei aufgeständerten Objekten, baseY > 0)
 *  3. Schatten = Vereinigung aus Nah-/Fern-Footprint + Kanten-Quads
 *     (polygon-clipping; deckt konkave Footprints ab)
 *  4. Pflanzen: Krone als 14-Eck (Kronendurchmesser), Zylinder-Näherung
 *  5. altitude ≤ Mindesthöhe → kein Schatten (Nacht/Dämmerung)
 *
 * Belaubung: Pflanzen tragen den foliageFactor als `weight`; Strukturen immer
 * 1.0. Gleiche Gewichte werden zu einer Fläche vereinigt — im Momentschatten
 * steuert das Gewicht die Opacity (#605), in der Aggregation die Beschattung (#606).
 *
 * Terrain (#631): Mit `options.terrain` ({ elevationAt, minLevel }) landen die
 * Schatten per First-Hit auf den echten Terrain-Niveaus (terrainProjection) —
 * inkl. Knick an Plateau-Kanten. Ohne Terrain (oder Zenit) läuft exakt der
 * flache v1-Pfad. Restvereinfachung v1 (Plan-Checkpoint): der Schattenrand ist
 * ein Randpolygon — sonnige Inseln IM Schatteninneren (Hügel über dem Strahl)
 * entstehen nicht.
 *
 * Vereinfachungen (bewusst): Zylinder statt Kronenform, keine
 * Diffuslicht-Modellierung.
 */

import polygonClipping from 'polygon-clipping';
import { foliageFactor } from './foliage';
import { getPlantShadowVolumes } from '../plantArchetypes';
import {
  shadowRay,
  projectPointToTerrain,
  subdivideRing,
  pointsOnLevel,
} from './terrainProjection';

// Unter dieser Sonnenhöhe wird kein Schatten gezeichnet: die Länge liefe gegen
// unendlich (flache Sonne), das Ergebnis wäre optisch unbrauchbar. ~3° über
// dem Horizont ist eine ruhige Grenze zwischen „noch Tag" und Dämmerung.
export const MIN_SUN_ALTITUDE_RAD = (3 * Math.PI) / 180;
const MIN_SUN_Y = Math.sin(MIN_SUN_ALTITUDE_RAD);

const CROWN_SEGMENTS = 14;
const MIN_FOOTPRINT_AREA_M2 = 1e-4; // 1 cm² — darunter degeneriert

/** Schattenversatz (Weltkoordinaten x/z) eines Punkts in Höhe h. */
export function shadowOffset(sunDirection, h) {
  const factor = h / sunDirection.y;
  return { x: -sunDirection.x * factor, z: -sunDirection.z * factor };
}

/** Shoelace-Fläche eines Rings [{x,z}]. */
function polygonArea(points) {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.z - b.x * a.z;
  }
  return Math.abs(area) / 2;
}

/** Ring [{x,z}] als polygon-clipping-Ring [[x,z], …]. */
function toRing(points) {
  return points.map((p) => [p.x, p.z]);
}

/** Footprint um (dx,dz) verschieben. */
function translate(points, dx, dz) {
  return points.map((p) => ({ x: p.x + dx, z: p.z + dz }));
}

/** Konvexer Footprint? (gleiches Kreuzprodukt-Vorzeichen an allen Ecken) */
function isConvex(points) {
  const n = points.length;
  if (n < 4) return true; // Dreieck ist immer konvex
  let sign = 0;
  for (let i = 0; i < n; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const c = points[(i + 2) % n];
    const cross = (b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x);
    if (Math.abs(cross) < 1e-12) continue; // kollinear
    const s = Math.sign(cross);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

/** Konvexe Hülle (Monotone Chain) als {x,z}-Ring, gegen den Uhrzeigersinn. */
function convexHull(points) {
  const arr = points.slice().sort((p, q) => p.x - q.x || p.z - q.z);
  const cross = (o, a, b) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
  const lower = [];
  for (const p of arr) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper = [];
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    const p = arr[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** Regelmäßiges n-Eck (Kronen-Footprint) um ein Zentrum. */
function ngon(center, radius, segments) {
  const pts = [];
  for (let i = 0; i < segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    pts.push({ x: center.x + Math.cos(a) * radius, z: center.z + Math.sin(a) * radius });
  }
  return pts;
}

/** Achsenparalleles Quadrat (Stamm-Footprint) um ein Zentrum, halbe Kante h. */
function square(center, h) {
  return [
    { x: center.x - h, z: center.z - h },
    { x: center.x + h, z: center.z - h },
    { x: center.x + h, z: center.z + h },
    { x: center.x - h, z: center.z + h },
  ];
}

/**
 * Schwenkfläche eines Casters zwischen Nah- und Fern-Footprint, als
 * polygon-clipping-MultiPolygon.
 *
 * Konvexe Footprints (Pflanzen-Krone, Rechtecke — der Regelfall) nehmen die
 * konvexe Hülle aus Nah- + Fern-Ecken: ein sauberes Polygon ohne
 * Vereinigungs-Rechnung, was den polygon-clipping-Präzisionsfall bei vielen
 * kantengleichen Quads (symmetrische Eingaben um den Nullpunkt) umgeht.
 * Konkave Footprints (z. B. L-Gebäude) fallen auf die Vereinigung aus
 * Nah-/Fern-Footprint + Kanten-Quads zurück.
 */
function sweptGeometry(footprint, nearOffset, farOffset, warnings) {
  const near = translate(footprint, nearOffset.x, nearOffset.z);
  const far = translate(footprint, farOffset.x, farOffset.z);

  if (isConvex(footprint)) {
    return [[toRing(convexHull([...near, ...far]))]];
  }

  const pieces = [[toRing(near)], [toRing(far)]];
  for (let i = 0; i < footprint.length; i += 1) {
    const j = (i + 1) % footprint.length;
    pieces.push([toRing([near[i], near[j], far[j], far[i]])]);
  }
  try {
    return polygonClipping.union(pieces[0], ...pieces.slice(1));
  } catch {
    warnings.push('shadow-sweep-failed');
    return [];
  }
}

/**
 * Schatten-Caster (Footprint + Höhe + Gewicht) aus dem SceneModel sammeln.
 *
 * Höhen-Konvention: Im flachen Modus zählt NUR die Höhe über der eigenen
 * Standfläche (Geländehöhe abgezogen — sonst kippt der Schatten auf
 * abgesenkten Zonen um 180° in die Sonnenrichtung). Im Terrain-Modus (#631)
 * sind baseY/topY dagegen ABSOLUTE Welt-Höhen: die Projektion rechnet gegen
 * die echte Oberfläche, das Kipp-Problem verschwindet strukturell.
 */
function collectCasters(sceneModel, month, terrainMode) {
  const casters = [];

  for (const ex of sceneModel.extrusions ?? []) {
    if (!ex.castsShadow || !(ex.heightM > 0)) continue;
    if (!Array.isArray(ex.footprint) || ex.footprint.length < 3) continue;
    if (polygonArea(ex.footprint) < MIN_FOOTPRINT_AREA_M2) continue;
    const baseY = terrainMode
      ? (ex.baseY ?? 0)
      : (ex.baseY ?? 0) - (ex.groundY ?? 0);
    casters.push({
      footprint: ex.footprint,
      baseY,
      heightM: ex.heightM,
      weight: 1.0,
    });
  }

  for (const plant of sceneModel.plants ?? []) {
    if (!plant.castsShadow || !(plant.heightM > 0)) continue;
    const weight = foliageFactor(plant.evergreen, month);

    // Mehrere Volumen pro Pflanze (Baum: erhöhte Krone + Stamm), damit der
    // Schatten aus der wahren Höhe projiziert wird (kein Corona-Ring am Stamm).
    // fromY/toY sind relativ zur Pflanzenbasis. plant.baseY ist die Standhöhe:
    // Gelände (#602) plus Gefäß-Oberkante (#686), plant.groundY nur der
    // Geländeanteil. Im flachen Modus zählt wie bei Extrusionen die Höhe über
    // der eigenen Standfläche — der Gefäß-Anteil bleibt also stehen (ein
    // 1,2-m-Blumenkasten wirft Schatten aus 1,2 m), der Geländeanteil fällt weg.
    const plantGround = terrainMode
      ? (plant.baseY ?? 0)
      : (plant.baseY ?? 0) - (plant.groundY ?? 0);
    for (const vol of getPlantShadowVolumes(plant)) {
      const span = vol.toY - vol.fromY;
      if (!(span > 0)) continue;
      const footprint =
        vol.shape === 'square'
          ? vol.halfSize > 0 && square(plant.position, vol.halfSize)
          : vol.radius > 0 && ngon(plant.position, vol.radius, CROWN_SEGMENTS);
      if (!footprint) continue;
      casters.push({
        footprint,
        baseY: plantGround + vol.fromY,
        heightM: span,
        weight,
      });
    }
  }

  return casters;
}

/** Zentroid eines Footprints (arithmetisches Mittel — reicht als Anker). */
function footprintCenter(points) {
  let x = 0;
  let z = 0;
  for (const p of points) {
    x += p.x;
    z += p.z;
  }
  return { x: x / points.length, z: z / points.length };
}

/**
 * Terrain-genaue Schwenkfläche (#631): Silhouette subdividieren, jeden Punkt
 * per First-Hit auf die Oberfläche werfen, dann Nah-/Fern-Ring + Kanten-Quads
 * vereinigen. KEINE konvexe Hülle — der Schattenrand darf an Plateau-Kanten
 * einknicken (Dellen würden von der Hülle überbrückt).
 *
 * Fast-Path: Liegen die grob (flach) projizierten Punkte alle auf dem Niveau
 * der Caster-Basis, kreuzt der Schatten keine Kante — dann ist die flache
 * Schwenkfläche exakt und es wird weder marschiert noch subdividiert.
 */
function sweptGeometryTerrain(caster, ray, terrain, warnings) {
  const { footprint, baseY, heightM } = caster;
  const topY = baseY + heightM;
  const { elevationAt } = terrain;
  const refLevel = elevationAt(footprintCenter(footprint));

  // Flache Grob-Projektion relativ zur Caster-Standfläche (Probe-Kandidaten).
  const flatOffset = (h) => ({ x: ray.dx * (h / ray.dropPerMeter), z: ray.dz * (h / ray.dropPerMeter) });
  const offNear = flatOffset(Math.max(0, baseY - refLevel));
  const offFar = flatOffset(Math.max(0, topY - refLevel));
  const flatNear = translate(footprint, offNear.x, offNear.z);
  const flatFar = translate(footprint, offFar.x, offFar.z);

  if (pointsOnLevel([...flatNear, ...flatFar], elevationAt, refLevel)) {
    return sweptGeometry(footprint, offNear, offFar, warnings);
  }

  // Voller Pfad: grob projizieren (Längenschätzung), subdividieren, marschieren.
  const coarseFar = footprint.map((p) => projectPointToTerrain(p, topY, ray, terrain));
  const subRing = subdivideRing(footprint, coarseFar);
  const near = subRing.map((p) => projectPointToTerrain(p, baseY, ray, terrain));
  const far = subRing.map((p) => projectPointToTerrain(p, topY, ray, terrain));

  const pieces = [[toRing(near)], [toRing(far)]];
  for (let i = 0; i < subRing.length; i += 1) {
    const j = (i + 1) % subRing.length;
    const quad = [near[i], near[j], far[j], far[i]];
    if (polygonArea(quad) < MIN_FOOTPRINT_AREA_M2) continue;
    pieces.push([toRing(quad)]);
  }
  try {
    return polygonClipping.union(pieces[0], ...pieces.slice(1));
  } catch {
    warnings.push('shadow-sweep-failed');
    return [];
  }
}

/**
 * Schattengeometrie eines Zeitpunkts.
 *
 * @param {object} sceneModel - SceneModel (#600), Footprints in Metern
 * @param {object} options
 * @param {{x:number,y:number,z:number}} options.sunDirection - Einheitsvektor
 *   zur Sonne in Weltkoordinaten (aus getSunDirection3D, northAngle schon drin)
 * @param {number} options.month - Monat 0–11 (Belaubung)
 * @param {{elevationAt:Function, minLevel:number}|null} [options.terrain] -
 *   Terrain-Sampler (#631). Fehlt er (oder Zenit-Sonne), läuft der flache Pfad.
 * @returns {{ altitude:number, isDay:boolean, groups:Array<{weight:number, multiPolygon:number[][][][]}>, warnings:string[] }}
 *   multiPolygon im polygon-clipping-Format ([[[x,z],…ring],…]), Weltmeter.
 */
export function computeShadowGeometry(sceneModel, { sunDirection, month, terrain = null }) {
  const warnings = [];
  const altitude = Math.asin(Math.max(-1, Math.min(1, sunDirection.y)));

  if (sunDirection.y <= MIN_SUN_Y) {
    return { altitude, isDay: false, groups: [], warnings };
  }

  // Terrain-Modus nur mit Sampler UND horizontalem Sonnenanteil (Zenit: der
  // Schatten liegt im Footprint, Terrain ändert daran nichts).
  const ray = terrain?.elevationAt ? shadowRay(sunDirection) : null;
  const terrainMode = !!ray;

  const casters = collectCasters(sceneModel, month, terrainMode);

  // Nach Gewicht bündeln — gleiche Belaubung = eine Fläche (kein additives
  // Überlappen im Momentschatten). Pro Caster eine Schwenkfläche.
  const geomsByWeight = new Map();
  for (const caster of casters) {
    let geom;
    if (terrainMode) {
      geom = sweptGeometryTerrain(caster, ray, terrain, warnings);
    } else {
      const nearOffset = shadowOffset(sunDirection, caster.baseY);
      const farOffset = shadowOffset(sunDirection, caster.baseY + caster.heightM);
      geom = sweptGeometry(caster.footprint, nearOffset, farOffset, warnings);
    }
    if (geom.length === 0) continue;
    const bucket = geomsByWeight.get(caster.weight) ?? [];
    bucket.push(geom);
    geomsByWeight.set(caster.weight, bucket);
  }

  const groups = [];
  for (const [weight, geoms] of geomsByWeight) {
    if (geoms.length === 0) continue;
    let multiPolygon;
    if (geoms.length === 1) {
      // Ein Caster → keine Vereinigung nötig (robuster, spart Rechnung)
      multiPolygon = geoms[0];
    } else {
      try {
        multiPolygon = polygonClipping.union(geoms[0], ...geoms.slice(1));
      } catch {
        warnings.push('shadow-union-failed');
        continue;
      }
    }
    if (multiPolygon.length > 0) groups.push({ weight, multiPolygon });
  }

  // Kräftigere Flächen (höheres Gewicht) zuletzt → oben beim Zeichnen
  groups.sort((a, b) => a.weight - b.weight);

  return { altitude, isDay: true, groups, warnings };
}

export default computeShadowGeometry;
